import { gql } from '@apollo/client';
import {
  gqlRepresentativeListPaginatedResult,
  gqlOrganizationsByRoleSubmissionsAndNamePaginatedResult,
  gqlPageInput,
} from '@minecraft.graphql-types';

export const QUERY_PROJECT_ROLES = gql`
  query projectRoles($projectId: Float!) {
    projectRoles(id: $projectId) {
      id
      name
      internalName
      roleStatus {
        id
        code
      }
    }
  }
`;

export interface QueryRepListOutput {
  representativeLists: gqlRepresentativeListPaginatedResult;
}

export const QUERY_REPRESENTATIVE_LISTS = gql`
  query representativeLists($input: GetOrganizationRepresentativeListsInput) {
    representativeLists: getOrganizationRepresentativeLists(input: $input) {
      objects {
        id
        name
        __typename
      }
      __typename
    }
  }
`;

export const QUERY_DMAS = gql`
  query dmas {
    dmas {
      id
      name
      region {
        id
        name
        country {
          id
          code
          name
        }
      }
    }
  }
`;

export const QUERY_PROFILE_SEARCH_UNION_FILTER_BY_ROLE = gql`
  query getProfileSearchUnionFilterByRole($roleId: Int!) {
    getProfileSearchUnionFilterByRole(roleIds: [$roleId]) {
      __typename
      roleId
      talentUnions
      excludeTalentUnions
    }
  }
`;

export const QUERY_ROLE_DETAILS = gql`
  query roles(
    $roleId: ID!
    $withGenderAppearance: Boolean!
    $withEthnicAppearance: Boolean!
    $withPlayableAgeRange: Boolean!
    $withWorkingLocation: Boolean!
    $skipProjectReleaseLocations: Boolean!
  ) {
    roles(ids: [$roleId]) {
      id
      displayedRoleUnion
      genderAppearance @include(if: $withGenderAppearance) {
        id
        name
        code
      }
      ethnicAppearance @include(if: $withEthnicAppearance) {
        id
        name
        code
      }
      ageInYears @include(if: $withPlayableAgeRange)
      ageMinMonth @include(if: $withPlayableAgeRange)
      ageMaxMonth @include(if: $withPlayableAgeRange)
      publicationSettings @include(if: $withWorkingLocation) {
        publicationSetting {
          id
          name
        }
        dmas {
          id
          name
          region {
            id
            name
            country {
              id
              name
              code
            }
          }
        }
      }
      projectReleaseLocations @skip(if: $skipProjectReleaseLocations) @include(if: $withWorkingLocation) {
        id
        name
        region {
          id
          name
          country {
            id
            name
            code
          }
        }
      }
    }
  }
`;

export const QUERY_CONFLICTS = gql`
  query conflicts {
    conflicts {
      id
      code
      name
      enabled
      sortOrder
    }
  }
`;

export interface SearchRepsByNameInput {
  roleId: string | number;
  divisionOrAgencyName: string;
  page: gqlPageInput;
}

export interface SearchRepsByNameOutput {
  organizationsByRoleSubmissionsAndName: gqlOrganizationsByRoleSubmissionsAndNamePaginatedResult;
}

export const SEARCH_REPRESENTATIVES_BY_NAME = gql`
  query SEARCH_REPRESENTATIVES_BY_NAME($divisionOrAgencyName: String!, $page: PageInput!, $roleId: Int!) {
    organizationsByRoleSubmissionsAndName(page: $page, divisionOrAgencyName: $divisionOrAgencyName, roleId: $roleId) {
      totalCount
      objects {
        orgId
        submissionsCount
        organization {
          id
          name
          parentOrganization {
            id
            name
          }
        }
      }
    }
  }
`;

export const SEARCH_REPRESENTATIVES = gql`
  query searchRepresentatives($page: PageInput!, $input: SearchRepresentativeInput!) {
    searchRepresentatives(page: $page, input: $input) {
      totalCount
      organizations {
        id
        name
        parentOrganization {
          id
          name
        }
      }
    }
  }
`;

export const SEARCH_ORGANIZATIONS = gql`
  query getOrganizationsByRepTypeAndName($input: OrganizationsByRepTypeAndNameInput!) {
    getOrganizationsByRepTypeAndName(input: $input) {
      totalCount
      objects {
        organization {
          id
          name
        }
      }
    }
  }
`;

export const SEARCH_ROSTER_LISTS = gql`
  query SEARCH_ROSTER_LISTS {
    getOrganizationProfileLists(page: { pageSize: 100, pageNumber: 1 }) {
      objects {
        id
        name
      }
    }
  }
`;

export const QUERY_SCHEDULES = gql`
  query searchSchedules($projectId: Int!) {
    searchSchedules(input: { projectId: [$projectId] }, page: { pageNumber: 1, pageSize: 100 }) {
      totalCount
      schedules: objects {
        id
        name
        lastUpdated
        scheduleType
        isCanceled
      }
    }
  }
`;

export const QUERY_SESSIONS = gql`
  query searchSessions($projectId: Int!) {
    auditionSessionsByProject(projectId: [$projectId], pageInput: { pageNumber: 1, pageSize: 100 }) {
      totalCount
      sessions: objects {
        id
        name
        created
      }
    }
  }
`;

export const QUERY_PRESENTATIONS = gql`
  query searchPresentations($projectId: Int!) {
    getPresentationsByProject(projectId: $projectId) {
      id
      name
      createdAt
    }
  }
`;

export const QUERY_PROJECT_TYPES = gql`
  query projectTypes {
    projectTypes {
      __typename
      id
      name
      selectable
      code
      enabled
      sortOrder
      parentObject {
        __typename
        code
        id
        name
      }
    }
  }
`;

export const QUERY_PROJECT_STATUS = gql`
  query projectStatus {
    projectStatus {
      __typename
      id
      name
      enabled
      sortOrder
      code
    }
  }
`;

export const QUERY_GENDER_APPEARANCES = gql`
  query genderAppearances {
    genderAppearances {
      id
      code
      name
      enabled
      sortOrder
    }
  }
`;

export const SEEKING_REPRESENTATIONS_QUERY = gql`
  query SeekingRepresentations {
    seekingRepresentations {
      enabled
      name
      sortOrder
      id
    }
  }
`;

export const QUERY_GENDER_IDENTITIES = gql`
  query genderIdentities {
    genderIdentities {
      id
      code
      name
      enabled
      sortOrder
    }
  }
`;

export const QUERY_ETHNIC_APPEARANCES = gql`
  query ethnicAppearances {
    ethnicAppearances {
      id
      code
      name
      enabled
      sortOrder
    }
  }
`;
